import type {Evaluator, Version} from "@cm/graph-v3"
import {versionChain} from "@cm/graph-v3"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {z} from "zod/v4-mini"
import {MaterialNode} from "#template/nodes/material/material-node"
import type {OnFilterActiveContext, TemplateNodeContext, TemplateNodeDefinition, VisitorNodeResult} from "#template/template-node/types"
import {skipped} from "#template/template-node/constants"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {ImageLikeNode, MaterialLikeNode, NumberLike} from "#template/nodes/types"
import {imageLike} from "#template/nodes/image/fundamental"
import {materialLike} from "#template/nodes/material/fundamental"
import {numberLike} from "#template/nodes/number/fundamental"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {MaterialGraph} from "@cm/material-nodes"
import {TransformColorOverlay} from "#template/runtime-graph/nodes/transform-color-overlay"
import {visitNone} from "#template/template-node/visit-helpers"

export type OverlayMaterialColorParameters = {
    name: string
    material: MaterialLikeNode | null
    overlay: ImageLikeNode | null
    size: [NumberLike, NumberLike]
}

type V0 = {
    material: MaterialLikeNode | null
    overlay: ImageLikeNode | null
    size: [NumberLike, NumberLike]
}

type V1 = V0 & {name: string}

const v0: Version<V0, V1> = {
    toNextVersion: (parameters) => ({...parameters, name: "Overlay Material Color"}),
}

const overlayMaterialColorDefinition: TemplateNodeDefinition<OverlayMaterialColorParameters> = {
    nodeClass: "OverlayMaterialColor",
    validation: {
        parameters: z.object({
            name: z.string(),
            material: z.nullable(materialLike),
            overlay: z.nullable(imageLike),
            size: z.tuple([numberLike, numberLike]),
        }),
        returns: getTemplateNodeReturns(),
    },
    versionChain: versionChain([v0]),
}

@registerTemplateGraphNode
export class OverlayMaterialColor extends MaterialNode<OverlayMaterialColorParameters> {
    public override get definition() {
        return overlayMaterialColorDefinition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<OverlayMaterialColorParameters> {
        if (this.parameters.material === null || this.parameters.overlay === null) return skipped
        return visitNone(this.parameters)
    }

    public override evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderInlet<MaterialGraph | null> {
        const material = evaluator.evaluate(this.parameters.material)
        const [materialValid, materialInvalid] = scope.branch(material)

        const image = evaluator.evaluate(this.parameters.overlay)

        const [sizeXValid, sizeXInvalid] = scope.branch(evaluator.evaluate(this.parameters.size[0]))
        const [sizeYValid, sizeYInvalid] = scope.branch(evaluator.evaluate(this.parameters.size[1]))

        const result = scope.node(TransformColorOverlay, {
            material: materialValid,
            image,
            size: scope.tuple(sizeXValid, sizeYValid),
            useAlpha: true,
        }).outputMaterial
        return scope.phi(result, materialInvalid, sizeXInvalid, sizeYInvalid)
    }
}
